import { useLocalStorage } from "@mantine/hooks";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  DSCORE_OAUTH_CODE_VERIFIER_KEY,
  DSCORE_OAUTH_STATE_KEY,
} from "../constants/dscore";
import {
  DsCoreAuthorizationUrlDocument,
  RegenerateDsCoreTokensDocument,
} from "../generated/graphql";
import {
  generateCodeVerifier,
  generateState,
  getCodeChallenge,
} from "../utils/pkce";

// Must match a redirect URL registered on the DS Core developer portal (staging,
// and localhost:5174 where Labs runs next to the webapp)
const getRedirectUri = () =>
  `${window.location.protocol}//${window.location.host}/orders/callback/dscore`;

export const useDsCoreOAuth = () => {
  const apolloClient = useApolloClient();
  const [state, setState, deleteState] = useLocalStorage({
    key: DSCORE_OAUTH_STATE_KEY,
  });
  const [codeVerifier, setCodeVerifier, deleteCodeVerifier] = useLocalStorage({
    key: DSCORE_OAUTH_CODE_VERIFIER_KEY,
  });
  const [regenerateDsCoreTokens, { loading: isRegeneratingTokens }] =
    useMutation(RegenerateDsCoreTokensDocument);

  // Same split as 3Shape: the verifier and the state stay in this browser.
  // Unlike 3Shape, the back builds the URL: client id, scope and base URL
  // only live in its config.
  const getDsCoreOAuthUrl = async () => {
    const newCodeVerifier = generateCodeVerifier();
    const newState = generateState();
    setCodeVerifier(newCodeVerifier);
    setState(newState);

    const { data } = await apolloClient.query({
      query: DsCoreAuthorizationUrlDocument,
      variables: {
        redirectUri: getRedirectUri(),
        codeChallenge: await getCodeChallenge(newCodeVerifier),
        state: newState,
      },
      fetchPolicy: "network-only",
    });

    return data.dsCoreAuthorizationUrl;
  };

  return {
    resetFlow: () => {
      deleteState();
      deleteCodeVerifier();
    },
    getDsCoreOAuthUrl,
    state,
    codeVerifier,
    isRegeneratingTokens,
    regenerateDsCoreTokens: ({
      code,
      codeVerifier,
    }: {
      code: string;
      codeVerifier: string;
    }) =>
      regenerateDsCoreTokens({
        variables: { code, codeVerifier, redirectUri: getRedirectUri() },
      }),
  };
};
